import { Stack } from "@mui/material";
import { MerchantHeader } from "Components/Customer/Payment/MerchantHeader";
import { BankAccountSection } from "Components/Customer/Payment/PaymentForms/BankAccountSection";
import { PaySubmitButton } from "Components/Customer/Payment/PaymentForms/PaySubmitButton";
import {
  type StatusAlertKind,
  TransactionStatusAlert,
} from "Components/Shared/Payments/TransactionStatusAlert";
import { useIsMobile } from "Hooks/useIsMobile";
import {
  type BankAccountListItem,
  TransactionStatus,
  type TransactionDetailDto,
} from "Infrastructure/Api/Api";
import type { ReactNode } from "react";
import type { Control, FieldValues } from "react-hook-form";

const STATUS_TO_ALERT: Partial<Record<TransactionStatus, StatusAlertKind>> = {
  [TransactionStatus.Success]: "paid",
  [TransactionStatus.Cancelled]: "cancelled",
};

type Props<T extends FieldValues & { bankAccountPublicID?: string }> = {
  // Optional because the Variable Collector entry page has no transaction yet at mount time;
  transactionDetails?: TransactionDetailDto;
  payPointPublicID?: string;
  bankAccounts: Array<BankAccountListItem>;
  control: Control<T>;
  totalAmount: number;
  currencyCode?: string;
  isPaymentDisabled: boolean;
  isSubmitting: boolean;
  onSubmit: () => void;
  onBankAccountChange?: (bankAccountPublicID: string) => void;
  children: ReactNode;
};

export const PaymentLayout = <
  T extends FieldValues & { bankAccountPublicID?: string },
>({
  transactionDetails,
  payPointPublicID,
  bankAccounts,
  control,
  totalAmount,
  currencyCode,
  isPaymentDisabled,
  isSubmitting,
  onSubmit,
  onBankAccountChange,
  children,
}: Props<T>) => {
  const isMobile = useIsMobile();

  const transactionStatus = transactionDetails?.transactionStatus;
  const statusAlert = transactionStatus
    ? (STATUS_TO_ALERT[transactionStatus] ?? null)
    : null;

  return (
    <Stack
      component="form"
      onSubmit={onSubmit}
      px={2}
      gap={7}
      pb={isMobile ? "80px" : 0}
    >
      <MerchantHeader
        creditorName={transactionDetails?.creditorName}
        payPointPublicID={
          transactionDetails?.payPointPublicID ?? payPointPublicID
        }
        merchantIdentityUserPublicID={
          transactionDetails?.merchantIdentityUserPublicID
        }
        merchantProfilePicture={transactionDetails?.merchantProfilePicture}
      />
      <Stack gap={6}>
        {statusAlert && <TransactionStatusAlert status={statusAlert} />}
        {children}
        <BankAccountSection
          control={control}
          bankAccounts={bankAccounts}
          onBankAccountChange={onBankAccountChange}
        />
      </Stack>
      <PaySubmitButton
        totalAmount={totalAmount}
        isDisabled={isPaymentDisabled}
        isLoading={isSubmitting}
        currencyCode={currencyCode}
      />
    </Stack>
  );
};
